import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import {
  DITTO_RELAY,
  DITTO_STATS_PUBKEY,
  parseTrendingHashtags,
  type TrendingHashtag,
} from '@/lib/ditto';

/**
 * Trending hashtags from Ditto (NIP-32 kind 1985 labels, `#t` mark in the
 * `pub.ditto.trends` namespace). Returns the freshest label event's tags,
 * already sorted by total uses. Queried directly against the Ditto relay.
 */
export function useTrendingHashtags(limit = 12) {
  const { nostr } = useNostr();

  return useQuery<TrendingHashtag[]>({
    queryKey: ['nostr', 'trending-hashtags'],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [1985], authors: [DITTO_STATS_PUBKEY], '#l': ['#t'], limit: 4 }],
        { relays: [DITTO_RELAY], signal: AbortSignal.any([signal, AbortSignal.timeout(4000)]) },
      );
      if (events.length === 0) return [];
      // Multiple versions may be returned; use the most recent.
      const latest = events.reduce((a, b) => (b.created_at > a.created_at ? b : a));
      return parseTrendingHashtags(latest).slice(0, limit);
    },
    staleTime: 5 * 60 * 1000,
  });
}
